// ============================================================
// cfaDates.ts — the CFA card's "Signed on" checks (pure; cfaDates.test.ts).
// Amber warnings only — saving still works. Mirrors the backend's
// cfaDateIssues (lib/settlementInputs.ts), which puts the SAME warnings in
// the preview's list: keep the wording identical.
//   • signed before the case's accident date;
//   • signed in the future (after today);
//   • signed before 1 April 2013 (outside the 25% cap rules).
// Also the settlement DATE ORDER warnings + the "can't be in the future"
// blocks (below). Dates are "YYYY-MM-DD" strings, compared as text (same as
// the backend).
// ============================================================
export const CFA_CAP_RULES_FROM = '2013-04-01'
const ukDate = (iso: string): string => { const [y, m, d] = iso.split('-'); return `${d}/${m}/${y}` }
const isIsoDate = (s: string | null | undefined): s is string => typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s)

export const cfaDateWarnings = (signedOn: string | null | undefined, accidentDate: string | null | undefined, today: string): string[] => {
  if (!isIsoDate(signedOn)) return []
  const out: string[] = []
  if (isIsoDate(accidentDate) && signedOn < accidentDate) out.push(`Signed before the accident date (${ukDate(accidentDate)}). Check both dates.`)
  if (signedOn > today) out.push(`Signed on a date in the future (${ukDate(signedOn)}). Check the date.`)
  if (signedOn < CFA_CAP_RULES_FROM) out.push('Pre-April 2013 CFAs aren’t covered by the 25% cap rules. Check with a solicitor.')
  return out
}

// ── Date ORDER across the settlement (warnings) ───────────────
// Mirrors the backend's settlementDateIssues (lib/settlementInputs.ts) — the
// preview lists the same warnings with the same codes; keep the wording
// identical. Expected: accident ≤ CFA signed ≤ issued ≤ settlement; every
// other date between the accident and the settlement date.
export interface SettlementDates {
  accidentDate: string | null
  cfaSignedOn: string | null
  settlementDate: string
  issuedOn: string | null
  quantumDate: string | null
  cruCertificateDate: string | null
  recoveredCostsAgreedOn: string | null
  interims: { id: string; receivedOn: string | null }[]
}
export interface DateWarning { code: 'NO_ACCIDENT_DATE' | 'DATE_BEFORE_ACCIDENT' | 'DATE_BEFORE_CFA' | 'DATE_AFTER_SETTLEMENT'; field?: string; message: string }
export const NO_ACCIDENT_DATE_NOTE = 'No accident date on this case, so dates can’t be checked.'

export const settlementDateWarnings = (d: SettlementDates): DateWarning[] => {
  const out: DateWarning[] = []
  if (!isIsoDate(d.accidentDate)) out.push({ code: 'NO_ACCIDENT_DATE', message: NO_ACCIDENT_DATE_NOTE })
  const accident = isIsoDate(d.accidentDate) ? d.accidentDate : null
  const cfa = isIsoDate(d.cfaSignedOn) ? d.cfaSignedOn : null
  const checks: { field: string; what: string; date: string | null; afterSettlementOk?: boolean }[] = [
    { field: 'issuedOn', what: 'Claim issued', date: d.issuedOn },
    { field: 'quantumDate', what: 'Quantum valued', date: d.quantumDate },
    { field: 'cruCertificateDate', what: 'CRU certificate dated', date: d.cruCertificateDate },
    { field: 'recoveredCostsAgreedOn', what: 'Costs agreed', date: d.recoveredCostsAgreedOn },
    ...d.interims.map((p) => ({ field: `interim:${p.id}`, what: 'Interim payment received', date: p.receivedOn })),
    { field: 'settlementDate', what: 'Settlement date is', date: d.settlementDate, afterSettlementOk: true },
  ]
  for (const c of checks) {
    if (!isIsoDate(c.date)) continue
    if (accident && c.date < accident) {
      out.push({ code: 'DATE_BEFORE_ACCIDENT', field: c.field, message: `${c.what} before the accident date (${ukDate(accident)}). If the accident date is wrong, fix it in Accident details.` })
    } else if (cfa && c.date < cfa && c.field !== 'quantumDate' && c.field !== 'cruCertificateDate') {
      out.push({ code: 'DATE_BEFORE_CFA', field: c.field, message: `${c.what} before the CFA was signed (${ukDate(cfa)}). If the signed date is wrong, fix it in the CFA card.` })
    }
    if (!c.afterSettlementOk && c.date > d.settlementDate) {
      out.push({ code: 'DATE_AFTER_SETTLEMENT', field: c.field, message: `${c.what} after the settlement date (${ukDate(d.settlementDate)}). Check the dates.` })
    }
  }
  return out
}

// BLOCKED on the server (400) — shown at once on the page too.
export const futureDateError = (label: string, date: string | null | undefined, today: string): string | null =>
  isIsoDate(date) && date > today ? `${label} can’t be in the future.` : null
